// src/resources/dashboards/Painel360/RedemptionsSection.tsx
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem';
import dayjs from 'dayjs';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum } from './shared';
import { RedemptionsData, SectionState } from './types';

const SourceChip = ({ source }: { source: 'clube' | 'loja' }) => (
  <Box component="span" sx={{
    fontSize: 9.5, fontWeight: 700, px: 0.7, py: '1px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: 0.4,
    color: source === 'clube' ? COLORS.brand : COLORS.teal,
    bgcolor: source === 'clube' ? 'rgba(120,105,223,.12)' : 'rgba(20,184,166,.12)',
  }}>{source === 'clube' ? 'clube' : 'loja'}</Box>
);

const RedemptionsSection = ({ state }: { state: SectionState<RedemptionsData> }) => {
  const theme = useTheme();
  const chartFont = theme.typography.fontFamily;
  if (state.loading) return <SkeletonCard height={360} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={360} />;
  if (!state.data) return null;
  const { series, topProducts, breakage, totals } = state.data;

  const chartData = series.map(s => ({ ...s, label: dayjs(s.bucketStart).format('DD/MM') }));
  const maxUsed = Math.max(1, ...topProducts.map(p => p.used));

  return (
    <Box>
      <SectionHeader
        icon={<RedeemIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.brand}, ${COLORS.teal})`}
        title="Produtos & Resgates"
        subtitle="O que o cliente troca pontos por — campeões, tendência e desperdício"
      />

      <Box sx={{ ...cardSx, mb: 1.75 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Resgates no período</Typography>
        <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1 }}>
          Usados {fmtNum(totals.used)} · Clube {fmtNum(totals.clube)} · Loja de pontos {fmtNum(totals.loja)} · {fmtBrl(totals.value)} em prêmios
        </Typography>
        {!chartData.length ? <EmptyHint text="Sem resgates no período." /> : (
          <Box sx={{ width: '100%', height: 220, fontFamily: chartFont }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.muted, fontFamily: chartFont }} />
                <YAxis tick={{ fontSize: 11, fill: COLORS.muted, fontFamily: chartFont }} tickFormatter={v => fmtNum(v)} />
                <Tooltip formatter={(v: any) => fmtNum(v)} contentStyle={{ fontFamily: chartFont, fontSize: 13, borderRadius: 12, border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }} />
                <Legend wrapperStyle={{ fontFamily: chartFont }} />
                <Area type="monotone" dataKey="clube" name="Clube de Vantagens" stackId="1" stroke={COLORS.brand} fill={COLORS.brand} fillOpacity={0.4} />
                <Area type="monotone" dataKey="loja" name="Loja de pontos" stackId="1" stroke={COLORS.teal} fill={COLORS.teal} fillOpacity={0.4} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.75 }}>
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Top produtos resgatados</Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>Por quantidade usada no período</Typography>
          {!topProducts.length ? <EmptyHint text="Sem produtos resgatados no período." /> : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {topProducts.map(p => (
                <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12 }}>
                  <Box sx={{ width: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: COLORS.ink, fontWeight: 600 }}>
                    {p.name} <SourceChip source={p.source} />
                  </Box>
                  <Box sx={{ flex: 1, height: 14, bgcolor: '#f1f5f9', borderRadius: 7, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${Math.round((p.used / maxUsed) * 100)}%`, borderRadius: 7, background: `linear-gradient(90deg, ${COLORS.brand}, #9d8cff)` }} />
                  </Box>
                  <Box sx={{ width: 96, textAlign: 'right', color: COLORS.muted }}>{fmtNum(p.used)} · {fmtBrl(p.value)}</Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Taxa de resgate (emitido → usado)</Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>Coorte emitida no período · vermelho = expirado sem uso</Typography>
          {!breakage.length ? <EmptyHint text="Nada emitido no período." /> : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {breakage.map(b => (
                <Box key={b.key} sx={{ fontSize: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ fontWeight: 600, color: COLORS.ink }}>{b.name} <SourceChip source={b.source} /></Box>
                    <Box sx={{ fontWeight: 700, color: COLORS.success }}>{b.usedRate !== null ? `${Math.round(b.usedRate * 100)}%` : '—'}</Box>
                  </Box>
                  <Box sx={{ height: 8, bgcolor: '#fee2e2', borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${Math.round((b.usedRate ?? 0) * 100)}%`, bgcolor: COLORS.success, borderRadius: 4 }} />
                  </Box>
                  <Typography sx={{ fontSize: 10, color: COLORS.faint, mt: 0.4 }}>
                    {fmtNum(b.issued)} emitidos · {fmtNum(b.used)} usados
                    {b.expiredRate !== null && b.expiredRate > 0 ? ` · ${Math.round(b.expiredRate * 100)}% expirado${b.expiredRate >= 0.3 ? ' ⚠️' : ''}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RedemptionsSection;
